import React from 'react';
import AboutSection from '../components/AboutSection';
import SEO from '../components/SEO';

const ABOUT_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://phennx.io/about#webpage',
      url: 'https://phennx.io/about',
      name: 'About Phennx',
      description:
        'Learn how Phennx keeps clinical trial operations accountable, compliant, and visible so teams act with confidence.',
      isPartOf: { '@id': 'https://phennx.io/#website' },
      about: { '@id': 'https://phennx.io/#organization' },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': 'https://phennx.io/about#breadcrumbs',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://phennx.io/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'About',
          item: 'https://phennx.io/about',
        },
      ],
    },
  ],
};

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About Phennx"
        description="Learn how Phennx keeps clinical trial operations accountable, compliant, and visible so teams act with confidence."
        path="/about"
        image="/logo-full-transparent.png"
        type="website"
        schema={ABOUT_SCHEMA}
      />
      <header className="about-page-hero" aria-label="About Phennx overview">
        <div className="about-page-hero__glow" aria-hidden="true" />
        <div className="section-inner about-page-hero__inner">
          <div className="about-page-hero__copy">
            <div className="about-page-hero__eyebrow">About Phennx</div>
            <h1 className="about-page-hero__title">Clarity for the teams running clinical trials.</h1>
            <p className="about-page-hero__subtitle">
              We build systems that keep operations accountable, compliant, and visible so study teams can act faster with
              confidence.
            </p>
          </div>

          <div className="about-page-hero__graphic" aria-hidden="true">
            <svg viewBox="0 0 420 280" role="presentation">
              <defs>
                <linearGradient id="aboutOrb" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="rgba(255, 107, 53, 0.8)" />
                  <stop offset="100%" stopColor="rgba(255, 165, 0, 0.5)" />
                </linearGradient>
              </defs>
              <circle cx="210" cy="140" r="110" className="about-page-hero__orbit" />
              <circle cx="210" cy="140" r="70" className="about-page-hero__orbit about-page-hero__orbit--slow" />
              <path
                d="M40 190 C 140 60, 240 60, 320 140 S 380 230, 400 90"
                className="about-page-hero__trail"
                stroke="url(#aboutOrb)"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="8 12"
              />
              <circle cx="110" cy="96" r="7" className="about-page-hero__node" />
              <circle cx="250" cy="120" r="6" className="about-page-hero__node about-page-hero__node--delay" />
              <circle cx="360" cy="96" r="5" className="about-page-hero__node about-page-hero__node--slow" />
            </svg>
          </div>
        </div>

        <div className="section-inner">
          <div className="about-page-hero__stats" role="list" aria-label="What we deliver">
            <div className="about-page-stat" role="listitem">
              <div className="about-page-stat__value">Audit-ready</div>
              <div className="about-page-stat__label">Every action tracked</div>
            </div>
            <div className="about-page-stat" role="listitem">
              <div className="about-page-stat__value">Operational focus</div>
              <div className="about-page-stat__label">Less admin, more care</div>
            </div>
            <div className="about-page-stat" role="listitem">
              <div className="about-page-stat__value">Built by operators</div>
              <div className="about-page-stat__label">Site + sponsor insight</div>
            </div>
          </div>
        </div>
      </header>
      <AboutSection />
    </>
  );
}
